// Live "N processing…" indicator for the inbound pipeline (email + uploads).
// Polls /api/processing; when a job completes it pulls the new deal in via a
// refresh. Rendered in both the Inbox and Upload topbars.

import { useEffect, useRef, useState } from "react";
import { fetchProcessing } from "../api";
import { useAppData } from "../store";

export function ProcessingChip() {
  const { refresh, uploadJob } = useAppData();
  const [count, setCount] = useState(0);
  const seen = useRef<Set<string>>(new Set());
  const primed = useRef(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await fetchProcessing();
        if (!alive) return;
        setCount(d.processing.length);
        const done = d.recent.filter((r) => r.status === "done");
        const fresh = done.filter((r) => !seen.current.has(r.id));
        done.forEach((r) => seen.current.add(r.id));
        if (primed.current && fresh.length) void refresh();  // a deal just landed
        primed.current = true;
      } catch { /* polling is best-effort */ }
    };
    void tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [refresh]);

  // Show instantly for the user's own in-flight upload (the backend poll can lag
  // a few seconds), in addition to whatever the server reports (email + others).
  const localUploads = uploadJob?.phase === "working" ? 1 : 0;
  const display = Math.max(count, localUploads);
  if (display <= 0) return null;
  return (
    <>
      <style>{`@keyframes prism-spin{to{transform:rotate(360deg)}}`}</style>
      <span
        title="Inbound emails and uploads being processed"
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "4px 10px", borderRadius: 999, marginLeft: 8,
          background: "var(--warn-soft)", color: "var(--warn)",
          fontSize: 11.5, fontWeight: 500,
        }}
      >
        <span style={{
          width: 11, height: 11, borderRadius: "50%",
          border: "1.6px solid currentColor", borderTopColor: "transparent",
          display: "inline-block", animation: "prism-spin 0.7s linear infinite",
        }} />
        {display} {display === 1 ? "deal" : "deals"} processing…
      </span>
    </>
  );
}
